import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, UtensilsCrossed } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getPostLoginRoute } from "@/lib/permissions";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { rememberUiPack } from "@/lib/uiPackStorage";
import { toast } from "sonner";
import { useLocale } from "@/contexts/LocaleProvider";
import "@/styles/simple-ui.css";

export default function SimpleLogin() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@restaurant.local");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loginToken, setLoginToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const from = location.state?.from;

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (needs2fa) {
        const loggedIn = await verifyTwoFactor(loginToken, otpCode.trim());
        rememberUiPack(loggedIn?.company?.ui_pack);
        toast.success(t("common.welcomeBack"));
        const target = getPostLoginRoute(loggedIn?.permissions, from, loggedIn);
        navigate(target, { replace: true });
        return;
      }
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setNeeds2fa(true);
        setLoginToken(result.login_token);
        toast.info("Enter the 6-digit code from your authenticator app");
        return;
      }
      rememberUiPack(result?.company?.ui_pack);
      toast.success(t("common.welcomeBack"));
      const target = getPostLoginRoute(result?.permissions, from, result);
      navigate(target, { replace: true });
    } catch (err) {
      const message =
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.message ||
        "Login failed. Check your credentials.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="simple-login-bg simple-ui min-h-screen grid place-items-center p-4"
      dir={isRtl ? "rtl" : "ltr"}
      data-testid="simple-login-page"
    >
      <div className="w-full max-w-[420px]">
        <div className="mb-4 flex justify-end">
          <LanguageSwitcher className="border-white/30 bg-white/15 text-white hover:bg-white/25" />
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-violet-600 text-white">
              <UtensilsCrossed size={28} />
            </div>
            <h1 className="text-[22px] font-extrabold text-slate-900">{t("app.title")}</h1>
            <p className="mt-1 text-[14px] text-slate-500">{t("simple.loginSubtitle")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-[13px] font-semibold text-slate-600">
                {t("common.email")}
              </span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200"
                data-testid="login-email"
              />
            </label>

            {!needs2fa ? (
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-semibold text-slate-600">
                  {t("common.password")}
                </span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200"
                  data-testid="login-password"
                />
              </label>
            ) : (
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-semibold text-slate-600">
                  Authentication code
                </span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200"
                />
              </label>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-violet-600 text-[16px] font-bold text-white hover:bg-violet-700 disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {submitting ? t("common.signingIn") : t("common.signIn")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
